import { db, Timestamp } from '@/utils/Firebase'

const COLLECTION = 'aiRecommendations'

/**
 * Estructura de un documento en aiRecommendations/:
 * {
 *   coachId: string,
 *   studentId: string,
 *   month: string,          // 'YYYY-MM' — ej: '2025-07'
 *   summary: string,        // resumen narrativo
 *   changes: [              // cambios concretos
 *     { area: string, suggestion: string, priority: 'high' | 'medium' | 'low' }
 *   ],
 *   read: boolean,
 *   generatedAt: Timestamp,
 * }
 *
 * ID del documento: {coachId}_{studentId}_{month}
 */

export function recommendationDocId(coachId, studentId, month) {
    return `${coachId}_${studentId}_${month}`
}

export function currentMonth() {
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    return `${y}-${m}`
}

/**
 * Carga la recomendación del mes actual para un alumno.
 * Retorna null si no existe todavía.
 */
export async function loadRecommendation(coachId, studentId, month = null) {
    if (!coachId || !studentId) return null
    const m = month || currentMonth()
    const docId = recommendationDocId(coachId, studentId, m)
    const snap = await db.collection(COLLECTION).doc(docId).get()
    if (!snap.exists) return null
    return { id: snap.id, ...snap.data() }
}

/**
 * Marca la recomendación como leída.
 */
export async function markRecommendationRead(coachId, studentId, month = null) {
    if (!coachId || !studentId) return
    const m = month || currentMonth()
    const docId = recommendationDocId(coachId, studentId, m)
    await db.collection(COLLECTION).doc(docId).update({ read: true })
}

/**
 * Carga todas las recomendaciones no leídas del mes actual para un coach.
 * Se usa en Home para mostrar el badge de notificación.
 */
export async function loadUnreadRecommendations(coachId, month = null) {
    if (!coachId) return []
    const m = month || currentMonth()
    const snap = await db.collection(COLLECTION)
        .where('coachId', '==', coachId)
        .where('month', '==', m)
        .where('read', '==', false)
        .get()
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

/**
 * SOLO PARA DESARROLLO/TESTING — permite crear una recomendación
 * de prueba sin esperar al Cloud Scheduler del 1° de mes.
 * Eliminar o proteger con isDev antes de ir a producción.
 */
export async function createTestRecommendation(coachId, studentId) {
    if (!coachId || !studentId) return
    const m = currentMonth()
    const docId = recommendationDocId(coachId, studentId, m)
    await db.collection(COLLECTION).doc(docId).set({
        coachId,
        studentId,
        month: m,
        summary: 'El alumno mostró una adherencia del 75% durante el mes. Su bienestar general fue positivo aunque con algunos picos de fatiga en la semana 3. Se recomienda ajustar la carga en los días miércoles.',
        changes: [
            {
                area: 'Carga de trabajo',
                suggestion: 'Reducir el volumen del miércoles un 20% — los registros de wellness muestran fatiga recurrente ese día.',
                priority: 'high',
            },
            {
                area: 'Ejercicios de fuerza',
                suggestion: 'Incorporar una progresión en peso en sentadilla: el alumno lleva 3 semanas en el mismo peso sin señales de fatiga en ese ejercicio.',
                priority: 'medium',
            },
            {
                area: 'Recuperación',
                suggestion: 'Agregar un día de trabajo de movilidad — los scores de dolor escalan hacia el final de la semana.',
                priority: 'low',
            },
        ],
        assumedFields: ['objectives', 'adherence'],
        read: false,
        generatedAt: Timestamp.now(),
    })
}
